'use strict';

define('vb/action/builtin/loginAction',[
  'vb/action/action',
],
(Action) => {
  /** @typedef {import('vb/action/action').Outcome} Outcome */

  /**
   * Launch the login process as defined in the Security Provider implementation.
   * It invokes the handleLogin function on the Security Provider with the returnPath argument.
   * The returnPath parameter is used by the login page to return to the application
   * after a successfull login.
   *
   * @param {String} returnPath  the path of the page or flow to go to when login is successful
   *
   * Returns a 'failure' outcome in case of error or the outcome 'success' with the payload true if
   * navigation to the login page was successful.
   *
   * @extends Action
   * @category Action
   */
  class LoginAction extends Action {
    /**
     * Performs the login action by invoking the handleLogin function on the Security Provider.
     *
     * @param {Object} [parameters] - An object containing the returnPath parameter.
     * @param {string} [parameters.returnPath] - The path of the page or flow to navigate to after a successful login.
     *
     * @returns {Outcome} The outcome of the login operation.
     *                            Returns a failure outcome if an error occurs or the Security Provider is missing.
     *                            Otherwise, returns a success outcome with a boolean payload indicating whether
     *                            navigation to the login page was successful.
     *
     * @throws {Error} If an error occurs during the login operation.
     */
    perform(parameters) {
      const securityProvider = this.context.application.securityProvider;

      if (!securityProvider) {
        return Action.createFailureOutcome('Security Provider missing.');
      }

      try {
        return Action.createSuccessOutcome(securityProvider.handleLogin(parameters && parameters.returnPath));
      } catch (error) {
        return Action.createFailureOutcome('Error during login operation', error);
      }
    }

    /**
     * Sets the context for the LoginAction instance.
     *
     * @param {Object} context - The context object containing information about the application.
     * @returns {void}
     */
    setContext(context) {
      this.context = context;
    }
  }

  return LoginAction;
});

